import React, { useState, useEffect, useCallback } from 'react';
import { Button, Dropdown } from 'antd';
import Search from 'antd/es/transfer/search';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import GenericTable from './GenericTable';
import Pagination from '../layout/Pagination';
import Spinner from '../elements/Spinner/Spinner';
import PackageSubscriptionDetailsModal from '../modals/PackageSubscriptionDetailsModal';
import { fetchPackageSubscriptions } from '../../api/subscription-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import styles from '../../styles/subscriptions.module.scss';

const packageStatusMap = {
  pending_payment: { label: 'Na čekanju - plaćanje', bg: '#fff3e0', color: '#e65100' },
  active: { label: 'Aktivan', bg: '#e6f4ea', color: '#1e7e34' },
  expired: { label: 'Istekao', bg: '#fdecea', color: '#c0392b' },
};

const statusFilterOptions = [
  { label: 'Na čekanju - plaćanje', value: 'PENDING_PAYMENT' },
  { label: 'Aktivan', value: 'ACTIVE' },
  { label: 'Istekao', value: 'EXPIRED' },
];

const renderStatusPill = (val) => {
  const key = val ? val.toLowerCase() : '';
  const { label, bg, color } = packageStatusMap[key] ?? { label: val ?? '-', bg: '#f0f0f0', color: '#555555' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        backgroundColor: bg,
        color,
      }}
    >
      {label}
    </span>
  );
};

const PackageSubscriptionsTable = () => {
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [detailsTarget, setDetailsTarget] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      if (search !== debouncedSearch) {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const loadPackageSubscriptions = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await fetchPackageSubscriptions({
      page: currentPage,
      limit: 10,
      status,
      search: debouncedSearch,
    });
    if (success) {
      // GenericTable keys/targets rows by `_id`; the backend DTO uses `id`.
      setItems(data.items.map((item) => ({ ...item, _id: item.id })));
      setTotalPages(data.totalPages);
    } else {
      toast.error(message || 'Greška pri učitavanju pretplata na pakete.');
    }
    setLoading(false);
  }, [currentPage, status, debouncedSearch]);

  useEffect(() => {
    loadPackageSubscriptions();
  }, [loadPackageSubscriptions]);

  const columns = [
    {
      key: 'user',
      label: 'Korisnik',
      render: (val) => <span>{`${val?.firstName || ''} ${val?.lastName || ''}`.trim() || '-'}</span>,
    },
    {
      key: 'email',
      label: 'Email',
      render: (val, row) => <span>{row?.user?.email || '-'}</span>,
    },
    {
      key: 'packages',
      label: 'Paketi',
      render: (val) => (
        <span>
          {val && val.length > 0
            ? val
                .map((p) =>
                  p.quantity > 1 ? `${p.name} × ${p.quantity}` : p.name
                )
                .join(', ')
            : '-'}
        </span>
      ),
    },
    {
      key: 'durationMonths',
      label: 'Trajanje',
      render: (val) => <span>{val} meseci</span>,
    },
    {
      key: 'totalAtPurchase',
      label: 'Ukupno plaćeno',
      render: (val) => <span>{formatPriceWithCurrency(val ?? 0)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => renderStatusPill(val),
    },
    {
      key: 'fulfilledCycles',
      label: 'Napredak',
      render: (val, row) => <span>{val}/{row.totalCycles}</span>,
    },
    {
      key: 'nextFulfillmentDate',
      label: 'Sledeće slanje',
      render: (val, row) => {
        if (row.status !== 'ACTIVE' || !val) return <span>-</span>;
        return <span>{dayjs(val).format('DD.MM.YYYY.')}</span>;
      },
    },
  ];

  const actions = [
    {
      key: 'details',
      label: 'Detalji',
      icon: <img src="/assets/icons/view-icon.svg" alt="Detalji" />,
      onClick: (row) => setDetailsTarget(row),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <Dropdown
          menu={{
            items: statusFilterOptions.map((option) => ({
              key: option.value,
              label: option.label,
              onClick: () => setStatus(option.value),
            })),
          }}
          trigger={['click']}
          placement="bottomLeft"
          className={'btn-filter-container'}
        >
          <Button className={`btn-tertiary btn-filter ${status ? 'active' : ''}`}>
            {status ? `Status: ${statusFilterOptions.find((o) => o.value === status)?.label}` : 'Status'}
            {status && (
              <img
                src="/assets/icons/x-gray.svg"
                title="Ukloni filter status"
                style={{ width: '10px', height: '10px', marginRight: '5px', cursor: 'pointer' }}
                alt="Ukloni filter"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatus(null);
                }}
              />
            )}
          </Button>
        </Dropdown>
        <div>
          <Search
            placeholder="Pretraži"
            className="search-input"
            allowClear
            enterButton
            onChange={(e) => setSearch(e.target.value)}
            width="auto"
          />
        </div>
      </div>

      {loading ? (
        <Spinner size="lg" />
      ) : (
        <>
          <GenericTable
            data={items}
            columns={columns}
            actions={actions}
            emptyState={{
              icon: '/assets/icons/no-users.svg',
              title: 'Nema pretplata na pakete',
            }}
          />
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}

      <PackageSubscriptionDetailsModal
        visible={!!detailsTarget}
        packageSubscription={detailsTarget}
        onClose={() => setDetailsTarget(null)}
      />
    </div>
  );
};

export default PackageSubscriptionsTable;
